import { KeyboardEvent, ReactNode, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";

/**
 * Finestra modale del modulo Ritiri valori (dettaglio sul telefono, legenda).
 * `Modal` e `SlideOver` dell'app non contengono il fuoco e non lo restituiscono,
 * quindi la gestione sta qui, in un solo posto:
 * - `role="dialog"`, `aria-modal`, `aria-labelledby` sul titolo;
 * - il resto della pagina è `inert` e non scorre;
 * - Tab e Maiusc+Tab girano solo tra i controlli della finestra;
 * - Esc chiude; alla chiusura il fuoco torna dov'era prima dell'apertura.
 */

const FOCALIZZABILI =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Tab e Maiusc+Tab: il prossimo controllo nell'ordine del documento, girando in fondo e in cima. */
function intrappolaFuoco(e: KeyboardEvent<HTMLDivElement>) {
  if (e.key !== "Tab") return;
  const finestra = e.currentTarget;
  const controlli = [...finestra.querySelectorAll<HTMLElement>(FOCALIZZABILI)].filter((el) => el.getClientRects().length > 0);
  if (controlli.length === 0) {
    e.preventDefault();
    return;
  }
  // Si parte anche dal titolo (tabindex -1), che non è tra i controlli.
  const attivo = document.activeElement;
  let prossimo: HTMLElement;
  if (!(attivo instanceof HTMLElement) || !finestra.contains(attivo)) {
    prossimo = e.shiftKey ? controlli[controlli.length - 1] : controlli[0];
  } else if (e.shiftKey) {
    const prima = controlli.filter((c) => attivo.compareDocumentPosition(c) & Node.DOCUMENT_POSITION_PRECEDING);
    prossimo = prima[prima.length - 1] ?? controlli[controlli.length - 1];
  } else {
    prossimo = controlli.find((c) => attivo.compareDocumentPosition(c) & Node.DOCUMENT_POSITION_FOLLOWING) ?? controlli[0];
  }
  e.preventDefault();
  prossimo.focus();
}

interface Props {
  /** Id del titolo della finestra: `aria-labelledby` e primo fuoco. */
  idTitolo: string;
  onChiudi: () => void;
  /** `pagina`: a tutta pagina (telefono). `pannello`: pannello a destra con velo (schermo largo). */
  variante: "pagina" | "pannello";
  children: ReactNode;
}

export function FinestraRitiri({ idTitolo, onChiudi, variante, children }: Props) {
  const finestra = useRef<HTMLDivElement>(null);
  const chiudi = useRef(onChiudi);
  chiudi.current = onChiudi;

  useEffect(() => {
    const precedente = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const contenitore = finestra.current?.parentElement ?? null;

    // Sfondo inerte: tutti i figli di `body` tranne quello della finestra.
    const resi: Element[] = [];
    for (const el of Array.from(document.body.children)) {
      if (el === contenitore || el.hasAttribute("inert")) continue;
      el.setAttribute("inert", "");
      resi.push(el);
    }
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    document.getElementById(idTitolo)?.focus();

    const tasto = (e: globalThis.KeyboardEvent) => e.key === "Escape" && chiudi.current();
    window.addEventListener("keydown", tasto);
    return () => {
      window.removeEventListener("keydown", tasto);
      for (const el of resi) el.removeAttribute("inert");
      document.body.style.overflow = overflow;
      if (precedente?.isConnected) precedente.focus();
    };
  }, [idTitolo]);

  return createPortal(
    <div className={clsx("fixed inset-0 z-panel", variante === "pannello" && "flex justify-end")}>
      {variante === "pannello" && <div className="absolute inset-0 bg-ink/45 backdrop-blur-[2px]" aria-hidden="true" onClick={onChiudi} />}
      <div
        ref={finestra}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitolo}
        onKeyDown={intrappolaFuoco}
        className={clsx(
          "relative h-full overflow-y-auto bg-surface",
          variante === "pagina" ? "w-full px-4 pb-10 pt-5" : "w-full max-w-2xl border-l border-ritiri-bordo px-6 pb-10 pt-6 shadow-lift"
        )}
      >
        {children}
      </div>
    </div>,
    document.body
  );
}
